import axios from "axios";

const API_URL = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
const INPUT_TYPE = "JSON";
const RESPONSE_TYPE = "JSON";
const GET_ENTRY_METHOD = "get_entry_list";
const FAMILY_TREE_GRAPH_ENDPOINT = "https://seltentechnologiesdemo.com/moona-backend/getFamilyTree.php";

const USER_PROFILE_FIELDS = [
  "id",
  "user_name",
  "first_name",
  "last_name",
  "email_c",
  "phone_mobile",
  "address_street",
  "address_state",
  "address_country",
  "address_city",
  "address_postalcode",
  "aadhaar_c",
  "caste_c",
  "religion_c",
  "age_c",
  "alivestatus_c",
  "birthplace_c",
  "bloodgroup_c",
  "dateofdeath_c",
  "deathplace_c",
  "deathreason_c",
  "disability_c",
  "disabilitytype_c",
  "dob_c",
  "gender_c",
  "relationship",
  "relative_person",
  "spouseid_c",
  "parentid_c",
  "languagesknown_c",
  "mothertongue_c",
  "maritalstatus_c",
  "marriagedate_c",
  "marriageplace_c",
  "economical_status_c",
  "photo_url_c",
];

const getFieldValue = (fields, key) => (fields?.[key]?.value ?? "");

const buildRestParams = (method, data) => {
  const params = new URLSearchParams();
  params.append("method", method);
  params.append("input_type", INPUT_TYPE);
  params.append("response_type", RESPONSE_TYPE);
  params.append("rest_data", JSON.stringify(data));
  return params;
};

const ensureSession = (sessionId) => {
  if (!sessionId) {
    throw new Error("Session ID is required for this operation");
  }
};

const ensureUserId = (userId) => {
  if (!userId) {
    throw new Error("User ID is required for this operation");
  }
};

export const callFamilyTreeApi = async (method, data, retries = 2) => {
  const params = buildRestParams(method, data);

  try {
    const response = await axios.post(API_URL, params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    if (!response?.data) {
      throw new Error("Empty response from server");
    }

    return response.data;
  } catch (error) {
    if (retries > 0) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return callFamilyTreeApi(method, data, retries - 1);
    }
    throw error;
  }
};

export const fetchFamilyMembers = async (sessionId, userId) => {
  ensureSession(sessionId);
  ensureUserId(userId);

  const restData = {
    session: sessionId,
    module_name: "FT_FamilyTree",
    query: `users_id_c = '${userId}'`,
    order_by: "date_entered DESC",
    deleted: 0,
  };

  const response = await callFamilyTreeApi(GET_ENTRY_METHOD, restData);
  const entries = Array.isArray(response?.entry_list) ? response.entry_list : [];

  return entries.map((entry) => {
    const fields = entry?.name_value_list || {};

    return {
      id: entry?.id || getFieldValue(fields, "id"),
      name: getFieldValue(fields, "name"),
      relationship: getFieldValue(fields, "relationship_type_c"),
      userId: getFieldValue(fields, "users_id_c"),
      aadhar: getFieldValue(fields, "aadhar_c"),
      address: getFieldValue(fields, "address_c"),
      caste: getFieldValue(fields, "caste_c"),
      city: getFieldValue(fields, "city_c"),
      pincode: getFieldValue(fields, "pincode_c"),
      country: getFieldValue(fields, "country_c"),
      mobile: getFieldValue(fields, "mobile_c"),
      email: getFieldValue(fields, "email_c"),
      religion: getFieldValue(fields, "religion_c"),
      state: getFieldValue(fields, "state_c"),
      age_c: getFieldValue(fields, "age_c"),
      alivestatus_c: getFieldValue(fields, "alivestatus_c"),
      birthplace_c: getFieldValue(fields, "birthplace_c"),
      bloodgroup_c: getFieldValue(fields, "bloodgroup_c"),
      dateofdeath_c: getFieldValue(fields, "dateofdeath_c"),
      deathplace_c: getFieldValue(fields, "deathplace_c"),
      deathreason_c: getFieldValue(fields, "deathreason_c"),
      disability_c: getFieldValue(fields, "disability_c"),
      disabilitytype_c: getFieldValue(fields, "disabilitytype_c"),
      dob_c: getFieldValue(fields, "dob_c"),
      gender_c: getFieldValue(fields, "gender_c"),
      languagesknown_c: getFieldValue(fields, "languagesknown_c"),
      mothertongue_c: getFieldValue(fields, "mothertongue_c"),
      maritalstatus_c: getFieldValue(fields, "maritalstatus_c"),
      marriagedate_c: getFieldValue(fields, "marriagedate_c"),
      marriageplace_c: getFieldValue(fields, "marriageplace_c"),
      economical_status_c: getFieldValue(fields, "economical_status_c"),
      spouseid_c: getFieldValue(fields, "spouseid_c"),
      parentid_c: getFieldValue(fields, "parentid_c"),
      family_group_id_c: getFieldValue(fields, "family_group_id_c"),
      linkedUserId: getFieldValue(fields, "linked_user_id_c") || getFieldValue(fields, "linked_user_id") || getFieldValue(fields, "linked_userid_c") || getFieldValue(fields, "linked_userid"),
      photo: getFieldValue(fields, "photo_url_c") || null,
      photo_url_c: getFieldValue(fields, "photo_url_c") || null,
    };
  });
};

export const fetchUserProfile = async (sessionId, userId) => {
  ensureSession(sessionId);
  ensureUserId(userId);

  const restData = {
    session: sessionId,
    module_name: "Users",
    query: `users.id = '${userId}'`,
    select_fields: USER_PROFILE_FIELDS,
  };

  const response = await callFamilyTreeApi(GET_ENTRY_METHOD, restData);
  const entry = Array.isArray(response?.entry_list) && response.entry_list.length > 0
    ? response.entry_list[0]
    : null;

  return entry?.name_value_list || null;
};

export const deleteFamilyMember = async (sessionId, memberId) => {
  ensureSession(sessionId);
  if (!memberId) {
    throw new Error("Member ID is required to delete a record");
  }

  const restData = {
    session: sessionId,
    module_name: "FT_FamilyTree",
    name_value_list: [
      { name: "id", value: memberId },
      { name: "deleted", value: 1 },
    ],
  };

  const response = await callFamilyTreeApi("set_entry", restData);
  if (!response?.id) {
    throw new Error("Failed to delete family member");
  }
  return response.id;
};

export const fetchFamilyTreeGraph = async (familyGroupId) => {
  console.log('fetchFamilyTreeGraph called with:', familyGroupId);
  if (!familyGroupId) {
    throw new Error("family_group_id_c is required to fetch the family tree graph");
  }

  const url = `${FAMILY_TREE_GRAPH_ENDPOINT}?family_group_id_c=${encodeURIComponent(familyGroupId)}`;
  console.log('fetchFamilyTreeGraph: calling url', url);
  const response = await axios.get(url);
  console.log('fetchFamilyTreeGraph response:', response?.data);
  return response?.data || null;
};

export const fetchAllFamilyMembers = async () => {
  const url = "https://seltentechnologiesdemo.com/moona-backend/customapi/getFamilyMembers.php";
  const response = await axios.get(url);
  console.log("Family members API response:", response.data);
  const entries = Array.isArray(response?.data) ? response.data : [];
  console.log("Parsed entries:", entries);
  if (entries.length === 0) {
    console.log("No family members found or response is not an array");
  }

  return entries.map((entry) => ({
    id: entry?.id || "",
    name: entry?.name || "",
    relationship: entry?.relationship_type_c || "",
    userId: entry?.users_id_c || "",
    aadhar: entry?.aadhar_c || "",
    address: entry?.address_c || "",
    caste: entry?.caste_c || "",
    city: entry?.city_c || "",
    pincode: entry?.pincode_c || "",
    country: entry?.country_c || "",
    mobile: entry?.mobile_c || "",
    email: entry?.email_c || "",
    religion: entry?.religion_c || "",
    state: entry?.state_c || "",
    age_c: entry?.age_c || "",
    alivestatus_c: entry?.alivestatus_c || "",
    birthplace_c: entry?.birthplace_c || "",
    bloodgroup_c: entry?.bloodgroup_c || "",
    dateofdeath_c: entry?.dateofdeath_c || "",
    deathplace_c: entry?.deathplace_c || "",
    deathreason_c: entry?.deathreason_c || "",
    disability_c: entry?.disability_c || "",
    disabilitytype_c: entry?.disabilitytype_c || "",
    dob_c: entry?.dob_c || "",
    gender_c: entry?.gender_c || "",
    languagesknown_c: entry?.languagesknown_c || "",
    mothertongue_c: entry?.mothertongue_c || "",
    maritalstatus_c: entry?.maritalstatus_c || "",
    marriagedate_c: entry?.marriagedate_c || "",
    marriageplace_c: entry?.marriageplace_c || "",
    economical_status_c: entry?.economical_status_c || "",
    spouseid_c: entry?.spouseid_c || "",
    parentid_c: entry?.parentid_c || "",
    family_group_id_c: entry?.family_group_id_c || "",
    photo: entry?.photo_url_c || null,
    photo_url_c: entry?.photo_url_c || null,
  }));
};
